import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiBell, FiBriefcase, FiCheck, FiCheckCircle, FiFileText, FiLayers, FiLoader, FiRefreshCw, FiX } from "react-icons/fi";
import "./DealDeskNotifications.css";

const labels = { idea: "Idea Builder", project: "Project Management", case: "Phase 2" };
const dateLabel = value => new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
const dateGroup = value => {
  const today = new Date(), yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const day = new Date(value).toDateString();
  return day === today.toDateString() ? "Today" : day === yesterday.toDateString() ? "Yesterday" : "Earlier";
};
const relativeDate = value => {
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 60000));
  return minutes < 1 ? "Just now" : minutes < 60 ? `${minutes}m ago` : minutes < 1440 ? `${Math.floor(minutes / 60)}h ago` : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
};
const sourceIcons = { idea: FiLayers, project: FiBriefcase, case: FiFileText };

export default function DealDeskNotifications({ requestJson, onOpen }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const trigger = useRef(null), dialog = useRef(null), closeButton = useRef(null), life = useRef(null), read = useRef(null), writing = useRef(false);
  const selection = useRef(filter); selection.current = filter;
  const expanded = useRef(open); expanded.current = open;

  const load = useCallback(async (before = null, quiet = false) => {
    if (!life.current || life.current.signal.aborted || writing.current) return;
    read.current?.abort();
    const controller = new AbortController(); read.current = controller;
    const currentLife = life.current;
    const abort = () => controller.abort();
    currentLife.signal.addEventListener("abort", abort, { once: true });
    if (!quiet) setLoading(true);
    try {
      const query = new URLSearchParams({ filter: selection.current });
      if (before) query.set("before", before);
      const response = await requestJson(`/backend/sow/notifications?${query}`, { signal: controller.signal });
      if (controller.signal.aborted || currentLife !== life.current) return;
      const data = response.data;
      if (!Array.isArray(data?.items) || data.items.length > 50 || !Number.isSafeInteger(data.unreadCount) || data.unreadCount < 0
        || data.items.some(item => !item || typeof item.id !== "string" || typeof item.author !== "string"
          || typeof item.title !== "string" || typeof item.preview !== "string" || !Number.isFinite(Date.parse(item.createdAt)))) throw new Error("Notifications could not be loaded.");
      setItems(current => before ? [...current, ...data.items.filter(item => !current.some(row => row.id === item.id))] : data.items);
      setCount(data.unreadCount); setNext(data.nextBeforeId); setError("");
    } catch (failure) {
      if (!controller.signal.aborted && currentLife === life.current) setError(failure.message || "Notifications are unavailable.");
    } finally {
      currentLife.signal.removeEventListener("abort", abort);
      if (!controller.signal.aborted && currentLife === life.current) setLoading(false);
    }
  }, [requestJson]);

  useEffect(() => {
    const controller = new AbortController(); life.current = controller;
    load();
    const refresh = () => { if (document.visibilityState !== "hidden" && !expanded.current) load(null, true); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      controller.abort(); read.current?.abort(); window.clearInterval(timer);
      window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const close = useCallback(() => { dialog.current?.close(); setOpen(false); trigger.current?.focus(); }, []);
  useEffect(() => {
    if (open) {
      if (!dialog.current?.open) { dialog.current?.showModal(); closeButton.current?.focus(); }
      load();
    }
  }, [open, filter, load]);
  useEffect(() => {
    if (!open) return;
    // Legacy app-level Escape handlers must not cancel an editor behind the inbox.
    const escape = event => {
      if (event.key === "Escape") { event.preventDefault(); event.stopImmediatePropagation(); close(); }
    };
    window.addEventListener("keydown", escape, true);
    return () => window.removeEventListener("keydown", escape, true);
  }, [open, close]);
  const trapFocus = event => {
    event.stopPropagation();
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), [tabindex="0"]')]
      .filter(element => element.getClientRects().length);
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const update = async (operation, item) => {
    if (writing.current || !life.current || life.current.signal.aborted) return;
    writing.current = true; read.current?.abort(); setLoading(false); setBusy(item?.id || "all"); setError("");
    const controller = life.current;
    try {
      const path = operation === "read-all" ? "read-all" : `${encodeURIComponent(item.id)}/${operation}`;
      const response = await requestJson(`/backend/sow/notifications/${path}`, { method: "POST", body: JSON.stringify({}), signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!Number.isSafeInteger(response.data?.unreadCount) || response.data.unreadCount < 0
        || (item && response.data.id !== item.id)) throw new Error("The notification update was not confirmed. Refresh before trying again.");
      setCount(response.data.unreadCount);
      setItems(current => current.map(row => !item || item.id === row.id ? { ...row, readAt: row.readAt || new Date().toISOString() } : row));
      if (operation === "open") { await onOpen(response.data.target); if (!controller.signal.aborted) close(); }
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.message || "The notification could not be updated.");
    } finally {
      writing.current = false;
      if (!controller.signal.aborted) { setBusy(""); if (operation === "read-all") load(); }
    }
  };
  const visible = filter === "unread" ? items.filter(item => !item.readAt) : items;

  return <>
    <button ref={trigger} type="button" className="deal-desk-notifications__trigger"
      title="Notifications"
      aria-label={`Notifications${count ? `, ${count} unread` : ""}`} aria-haspopup="dialog" aria-expanded={open}
      onClick={() => setOpen(true)}>
      <FiBell aria-hidden="true" />{count > 0 && <span className="deal-desk-notifications__badge" aria-hidden="true">{count > 99 ? "99+" : count}</span>}
    </button>
    {open && createPortal(<dialog ref={dialog} className="deal-desk-notifications" aria-labelledby="deal-desk-notifications-title" onKeyDown={trapFocus}
      onCancel={event => { event.preventDefault(); close(); }} onClick={event => { if (event.target === dialog.current) close(); }}>
      <div className="deal-desk-notifications__inner">
        <header><div><h2 id="deal-desk-notifications-title">Notifications</h2><p role="status">{error ? "Needs attention" : loading ? "Loading..." : count ? `${count} unread` : "All caught up"}</p></div>
          <button type="button" title="Refresh notifications" aria-label="Refresh notifications" disabled={loading || !!busy} onClick={() => load()}><FiRefreshCw className={loading ? 'deal-desk-notifications__spin' : undefined} /></button>
          <button ref={closeButton} type="button" title="Close notifications" aria-label="Close notifications" onClick={close}><FiX /></button>
        </header>
        <div className="deal-desk-notifications__toolbar">
          <div className="deal-desk-notifications__filters" role="group" aria-label="Notification filter">
            {["all", "unread"].map(value => <button type="button" key={value} aria-pressed={filter === value} disabled={!!busy}
              onClick={() => { if (value === filter) return; selection.current = value; setItems([]); setNext(null); setFilter(value); }}>{value === "all" ? "All" : "Unread"}</button>)}
          </div>
          <button type="button" className="deal-desk-notifications__read-all" disabled={!count || !!busy} onClick={() => update("read-all")}>
            <FiCheckCircle aria-hidden="true" />{busy === "all" ? "Updating..." : "Mark all read"}
          </button>
        </div>
        {error && <div role="alert" className="deal-desk-notifications__error">{error}<button type="button" onClick={() => load()} disabled={!!busy}>Retry</button></div>}
        <div className="deal-desk-notifications__list" aria-busy={loading || !!busy}>
          {loading && !items.length ? <div role="status" aria-label="Loading notifications" className="deal-desk-notifications__skeleton">{[1, 2, 3, 4].map(id => <div key={id}><i /><i /><i /></div>)}</div>
            : !visible.length && !error ? <div className="deal-desk-notifications__empty"><FiBell aria-hidden="true" /><h3>{filter === "unread" ? "No unread notifications" : "No mentions yet"}</h3></div>
            : visible.map((item, index) => {
              const SourceIcon = sourceIcons[item.target?.kind] || FiFileText;
              const group = dateGroup(item.createdAt);
              return <React.Fragment key={item.id}>
              {(index === 0 || dateGroup(visible[index - 1].createdAt) !== group) && <h3 className="deal-desk-notifications__group">{group}</h3>}
              <article className={item.readAt ? "" : "is-unread"}>
              <button type="button" className="deal-desk-notifications__item" disabled={!!busy || !item.target}
                onClick={() => update("open", item)} aria-label={`Open ${item.title}`}>
                <span className="deal-desk-notifications__avatar" aria-hidden="true">{item.author.split(/\s+/).map(word => word[0]).slice(0, 2).join("")}</span>
                <span className="deal-desk-notifications__copy"><span><strong>{item.author}</strong> mentioned you</span><b>{item.title}</b>
                  <span className="deal-desk-notifications__preview">{item.preview}</span>
                  <small><SourceIcon aria-hidden="true" />{labels[item.target?.kind] || "Discussion"}<time dateTime={item.createdAt} title={dateLabel(item.createdAt)}>{relativeDate(item.createdAt)}</time></small>
                </span>
              </button>
              {!item.readAt && <button type="button" className="deal-desk-notifications__mark" title="Mark as read"
                aria-label={`Mark ${item.title} as read`} disabled={!!busy} onClick={() => update("read", item)}>{busy === item.id ? <FiLoader className="deal-desk-notifications__spin" /> : <FiCheck />}</button>}
            </article></React.Fragment>;
            })}
          {next && <button type="button" className="deal-desk-notifications__more" disabled={loading || !!busy} onClick={() => load(next)}>{loading ? "Loading..." : "Load more"}</button>}
        </div>
      </div>
    </dialog>, document.body)}
  </>;
}
